import { call, put, select } from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import { selectDeviceType } from 'features/application/applicationSlice';
import { selectIsBreakoutRoom } from 'features/breakout-rooms/breakoutRoomsSlice';
import { selectRoomId } from 'features/room/roomSlice';
import { formatErrorForLogging, logger } from 'utils/logger';
import { DRAFTS_LOG, DRAFTS_TAG } from 'utils/whiteboard/drafts/draftsLog';
import { getBrowserId } from 'utils/getBrowserId';
import { deviceSupportsDrafts } from 'utils/whiteboard/drafts/deviceSupportsDrafts';
import { whiteboardDrafts } from 'utils/whiteboard/drafts/WhiteboardDraftStore';
import { WhiteboardDraft } from 'utils/whiteboard/drafts/types';
import {
  draftAttached,
  draftLoaded,
  draftsAvailabilityResolved,
} from 'features/whiteboard/drafts/whiteboardDraftsSlice';
import { disableWhiteboardDraftsSaga } from 'features/whiteboard/drafts/sagas/disableWhiteboardDraftsSaga';

export function* initWhiteboardDraftsSaga() {
  yield put(draftAttached(false));

  const deviceType: string = yield select(selectDeviceType);
  const isBreakoutRoom: boolean = yield select(selectIsBreakoutRoom);

  if (!deviceSupportsDrafts(deviceType) || isBreakoutRoom) {
    yield call(disableWhiteboardDraftsSaga);
    return;
  }

  const roomId: string = yield select(selectRoomId);

  try {
    const usable: boolean = yield call(whiteboardDrafts.init, `${roomId}:${getBrowserId()}`);

    if (!usable) {
      yield call(disableWhiteboardDraftsSaga);
      return;
    }

    yield call(whiteboardDrafts.sweep);

    const draft: WhiteboardDraft | undefined = yield call(whiteboardDrafts.getDraft);

    yield put(draftsAvailabilityResolved(true));
    yield put(draftLoaded(draft ?? null));
  } catch (error) {
    logger.remote(DRAFTS_LOG).error('[drafts] Could not open storage for this room', {
      roomId,
      error: formatErrorForLogging(error),
    });

    yield call(Sentry.captureException, error, {
      tags: { feature: DRAFTS_TAG, operation: 'init' },
      extra: { roomId },
    });

    yield call(disableWhiteboardDraftsSaga);
  }
}
